import React, { FC } from 'react'
import { Table, TableBody } from 'transfix-ui/components/Table'
import { Box, TableCell, TableHead, TableRow, Skeleton } from 'transfix-ui/core'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { TeamMemberTableRow } from 'apps/fleet-planner/components/team/TeamMemberTableRow'
import { ITeamMember } from 'apps/fleet-planner/types'
import { InfoIcon } from 'transfix-icons'
import { Tooltip } from 'transfix-ui'

interface ITeamMembersTableProps {
  /**
   * Should show the loading state?
   * @default false
   */
  isLoading?: boolean
  /**
   * Team members that will be listed
   */
  teamMembers: ITeamMember[]
  /**
   * Callback to handle when a team member is intended to edited
   */
  onTeamMemberEdit: (teamMember: ITeamMember) => void
  /**
   * Callback to be executed when a team member is just updated
   */
  onIsRowUpdated?: (teamMember: ITeamMember) => boolean
}

export const TeamMembersTable: FC<ITeamMembersTableProps> = ({
  isLoading = false,
  teamMembers = [],
  onTeamMemberEdit,
  onIsRowUpdated = () => false,
}) => {
  const isRowUpdated = (teamMember: ITeamMember) => onIsRowUpdated(teamMember)
  const createEditHandler = (teamMember: ITeamMember) => () => {
    onTeamMemberEdit(teamMember)
  }

  const TableCellSkeleton = () => (
    <TableCell>
      <Box p={1}>
        <Skeleton variant='text' animation='wave' width='100%' height='26px' />
      </Box>
    </TableCell>
  )
  const fmsTeamMembersShowDriverSmsOptInStatusEnabled = useFeatureFlag(
    'fmsTeamMembersShowDriverSmsOptInStatus'
  )

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Team Member</TableCell>
          <TableCell>Role(s)</TableCell>
          <TableCell>Carrier(s)</TableCell>
          {fmsTeamMembersShowDriverSmsOptInStatusEnabled && (
            <TableCell>
              <span className='pr2'>SMS Opt-in</span>
              <Tooltip trigger={<InfoIcon size='small' />}>
                Indicates if driver is opted into SMS messaging for non-transfix loads
              </Tooltip>
            </TableCell>
          )}
          <TableCell>Contact</TableCell>
          <TableCell /> {/* Actions column */}
        </TableRow>
      </TableHead>

      <TableBody>
        {isLoading
          ? Array.from({ length: 10 }, (_, rowIndex) => (
              <TableRow key={rowIndex}>
                <TableCellSkeleton />
                <TableCellSkeleton />
                {fmsTeamMembersShowDriverSmsOptInStatusEnabled && <TableCellSkeleton />}
                <TableCellSkeleton />
                <TableCellSkeleton />
              </TableRow>
            ))
          : teamMembers.map(teamMember => (
              <TeamMemberTableRow
                key={teamMember?.id}
                teamMember={teamMember}
                isUpdated={isRowUpdated(teamMember)}
                onEdit={createEditHandler(teamMember)}
              />
            ))}
      </TableBody>
    </Table>
  )
}
